/**
 * LoginPage 登入頁面元件
 *
 * 提供統一的登入介面：
 * - Google OAuth 登入
 * - Microsoft OAuth 登入
 * - 錯誤訊息顯示
 */

import { authStore, setAuthenticated, setError, clearError, logout, type AuthState } from '@/store/auth-store';
import { signInWithGoogle, signInWithMicrosoft } from '@/api/auth-api';

// ========================================================================
// 型別定義
// ========================================================================

export interface LoginPageOptions {
  /** 登入成功後的回呼 */
  onLoginSuccess?: () => void;
}

// ========================================================================
// LoginPage 類別
// ========================================================================

export class LoginPage {
  private element: HTMLDivElement;
  private errorElement: HTMLDivElement;
  private options: LoginPageOptions;
  private unsubscribe: (() => void) | null = null;

  constructor(options: LoginPageOptions = {}) {
    this.options = options;
    this.element = this.createElement();
    this.errorElement = this.element.querySelector('#loginError') as HTMLDivElement;

    this.bindEvents();
    this.subscribeToStore();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLDivElement {
    return this.element;
  }

  /**
   * 顯示登入頁面
   */
  show(): void {
    this.element.classList.remove('hidden');
  }

  /**
   * 隱藏登入頁面
   */
  hide(): void {
    this.element.classList.add('hidden');
  }

  /**
   * 掛載到容器
   */
  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  /**
   * 銷毀元件
   */
  destroy(): void {
    this.unsubscribe?.();
    this.element.remove();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private createElement(): HTMLDivElement {
    const element = document.createElement('div');
    element.id = 'loginPage';
    element.className = [
      'fixed',
      'inset-0',
      'z-50',
      'flex',
      'items-center',
      'justify-center',
      'bg-gray-50',
    ].join(' ');

    element.innerHTML = `
      <div class="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">RaisePlan</h1>
          <p class="text-gray-500">智能辦公空間規劃系統</p>
        </div>

        <div class="space-y-4">
          <!-- Google 登入按鈕 -->
          <button
            type="button"
            id="googleSignInBtn"
            class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span class="text-gray-700 font-medium">使用 Google 帳號登入</span>
          </button>

          <!-- 分隔線 -->
          <div class="flex items-center gap-3">
            <div class="flex-1 h-px bg-gray-200"></div>
            <span class="text-sm text-gray-400">或</span>
            <div class="flex-1 h-px bg-gray-200"></div>
          </div>

          <!-- Microsoft 登入按鈕 -->
          <button
            type="button"
            id="microsoftSignInBtn"
            class="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#F25022" d="M1 1h10v10H1z"/>
              <path fill="#00A4EF" d="M13 1h10v10H13z"/>
              <path fill="#7FBA00" d="M1 13h10v10H1z"/>
              <path fill="#FFB900" d="M13 13h10v10H13z"/>
            </svg>
            <span class="text-gray-700 font-medium">使用 Microsoft 帳號登入</span>
          </button>
        </div>

        <!-- 錯誤訊息 -->
        <div id="loginError" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm hidden">
        </div>

        <!-- 版權聲明 -->
        <p class="mt-8 text-center text-xs text-gray-400">
          登入即表示您同意我們的服務條款和隱私政策
        </p>
      </div>
    `;

    return element;
  }

  private bindEvents(): void {
    const googleBtn = this.element.querySelector('#googleSignInBtn');
    const microsoftBtn = this.element.querySelector('#microsoftSignInBtn');

    googleBtn?.addEventListener('click', () => this.handleGoogleLogin());
    microsoftBtn?.addEventListener('click', () => this.handleMicrosoftLogin());
  }

  private async handleGoogleLogin(): Promise<void> {
    try {
      clearError();
      const { user, token } = await signInWithGoogle();
      setAuthenticated(user, token);
      this.hide();
      this.options.onLoginSuccess?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : '登入失敗';
      setError(message);
    }
  }

  private async handleMicrosoftLogin(): Promise<void> {
    try {
      clearError();
      const { user, token } = await signInWithMicrosoft();
      setAuthenticated(user, token);
      this.hide();
      this.options.onLoginSuccess?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : '登入失敗';
      setError(message);
    }
  }

  private subscribeToStore(): void {
    this.unsubscribe = authStore.subscribe((state) => {
      // 更新錯誤訊息
      if (state.error) {
        this.errorElement.textContent = state.error;
        this.errorElement.classList.remove('hidden');
      } else {
        this.errorElement.classList.add('hidden');
      }

      // 🔒 根據認證狀態顯示/隱藏登入頁面
      if (state.isAuthenticated) {
        this.hide();
      } else {
        // 登出時重新顯示登入頁面
        this.show();
      }
    });
  }
}

// ========================================================================
// UserInfo 元件（Header 中的使用者資訊）
// ========================================================================

export interface UserInfoOptions {
  onLogout?: () => void;
  onSavePlan?: () => void;
  onLoadPlans?: () => void;
}

export class UserInfo {
  private element: HTMLDivElement;
  private options: UserInfoOptions;
  private unsubscribe: (() => void) | null = null;

  constructor(options: UserInfoOptions = {}) {
    this.options = options;
    this.element = this.createElement();
    this.bindEvents();
    this.subscribeToStore();
  }

  getElement(): HTMLDivElement {
    return this.element;
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  destroy(): void {
    this.unsubscribe?.();
    this.element.remove();
  }

  private createElement(): HTMLDivElement {
    const element = document.createElement('div');
    element.id = 'userInfo';
    element.className = 'flex items-center gap-3';

    element.innerHTML = `
      <span id="userEmail" class="text-sm font-medium text-gray-800"></span>
      <span id="currentPlanNameBadge" class="hidden px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full"></span>
      <button
        type="button"
        id="savePlanButton"
        class="hidden px-3 py-1.5 text-sm font-semibold text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
      >
        儲存規劃
      </button>
      <button
        type="button"
        id="savedPlansButton"
        class="hidden px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
      >
        已儲存的規劃
      </button>
      <button
        type="button"
        id="logoutButton"
        class="px-3 py-1.5 text-sm font-medium text-gray-800 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        登出
      </button>
    `;

    return element;
  }

  private bindEvents(): void {
    const logoutBtn = this.element.querySelector('#logoutButton');
    const saveBtn = this.element.querySelector('#savePlanButton');
    const loadBtn = this.element.querySelector('#savedPlansButton');

    logoutBtn?.addEventListener('click', () => this.handleLogout());
    saveBtn?.addEventListener('click', () => this.options.onSavePlan?.());
    loadBtn?.addEventListener('click', () => this.options.onLoadPlans?.());
  }

  private handleLogout(): void {
    // 清除認證狀態和 localStorage
    logout();
    this.options.onLogout?.();
  }

  private subscribeToStore(): void {
    // 定義更新 UI 的函式
    const updateUI = (state: AuthState) => {
      const emailEl = this.element.querySelector('#userEmail');
      const planBadge = this.element.querySelector('#currentPlanNameBadge');
      const saveBtn = this.element.querySelector('#savePlanButton');
      const loadBtn = this.element.querySelector('#savedPlansButton');

      if (emailEl) {
        emailEl.textContent = state.user?.email ?? '';
      }

      // 顯示/隱藏規劃相關按鈕
      if (state.isAuthenticated) {
        saveBtn?.classList.remove('hidden');
        loadBtn?.classList.remove('hidden');
      } else {
        saveBtn?.classList.add('hidden');
        loadBtn?.classList.add('hidden');
        planBadge?.classList.add('hidden');
      }
    };

    // 訂閱狀態變更
    this.unsubscribe = authStore.subscribe(updateUI);

    // 立即讀取並套用初始狀態
    updateUI(authStore.getState());
  }

  /**
   * 設定當前規劃名稱
   */
  setCurrentPlanName(name: string | null): void {
    const badge = this.element.querySelector('#currentPlanNameBadge');
    if (badge) {
      if (name) {
        badge.textContent = name;
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }
  }
}
